/**
 * QUILL EDITORS — инициализация rich-text редакторов.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: QUILL EDITORS).
 *
 * Sprint 18 (2026-05-09) — LAZY LOAD:
 *   Quill JS+CSS не подключаются в `<head>` (экономия 48KB gzip + ~1.3s
 *   cold-load на routes без редактора: dashboard, estimates list, tracker).
 *   Подгружаются динамически при первом вызове initQuillEditors().
 *   Триггер: shell.js при входе на route 'calculator'.
 *
 * Три редактора:
 *   - programDescEditor   → для описания программы тура (для клиента, идёт в печать)
 *   - quoteDescEditor     → для внутренних заметок (не печатается)
 *   - bookingTermsEditor  → для условий бронирования (печатается в КП)
 *
 * Если CDN недоступен — graceful degradation на обычные textarea.
 *
 * @module app/quill-editors
 */

const QUILL_JS_URL = '/lib/quill/quill.min.js';
const QUILL_CSS_URL = '/lib/quill/quill.snow.css';

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Динамическая загрузка Quill JS (idempotent, shared promise).
 * Возвращает промис, который резолвится после load события `<script>`.
 */
function loadQuillScript() {
    if (typeof window.Quill !== 'undefined') return Promise.resolve();
    if (window.__quillLoadPromise) return window.__quillLoadPromise;
    window.__quillLoadPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = QUILL_JS_URL;
        s.async = true;
        s.onload = () => {
            console.log('[Quill] script loaded dynamically');
            resolve();
        };
        s.onerror = () => {
            const err = new Error('Failed to load Quill script');
            console.warn('[Quill] dynamic script load failed', err);
            window.__quillLoadPromise = null; // allow retry
            reject(err);
        };
        document.head.appendChild(s);
    });
    return window.__quillLoadPromise;
}

/**
 * Динамическая загрузка Quill CSS (idempotent).
 * CSS не блокирует JS, но нужен для корректного отображения toolbar.
 */
function loadQuillCSS() {
    if (document.querySelector(`link[href="${QUILL_CSS_URL}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = QUILL_CSS_URL;
    document.head.appendChild(link);
}

/**
 * Прикрепляет Quill-методы к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachQuillEditors(ClassRef) {
    Object.assign(ClassRef.prototype, {
        async initQuillEditors() {
            // Идемпотентность: не инициализируем повторно
            if (this.quillEditors) {
                console.log('[Quill] редакторы уже инициализированы, skip');
                return;
            }

            console.log('[Quill] Инициализация редакторов (lazy load)...');

            // Старт CSS-загрузки параллельно (не блокирующий)
            loadQuillCSS();

            // Дожидаемся загрузки JS-библиотеки
            try {
                await loadQuillScript();
            } catch (err) {
                console.warn('[Quill] не удалось загрузить библиотеку — fallback на textarea', err);
                return;
            }

            if (typeof Quill === 'undefined') {
                console.warn('[Quill] Quill всё ещё не определён после load — fallback на textarea');
                return;
            }

            // Конфигурация тулбара
            const toolbarOptions = [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'indent': '-1'}, { 'indent': '+1' }],
                [{ 'align': [] }],
                ['link'],
                ['clean']
            ];

            const editorConfig = {
                theme: 'snow',
                modules: {
                    toolbar: toolbarOptions
                }
            };

            // Инициализация редактора для описания программы
            console.log('[Quill] Создаём programDescEditor...');
            const programDescEditor = new Quill('#program-description-editor', {
                ...editorConfig,
                placeholder: _t('app.quill.placeholder_program', 'Tour program description for the client')
            });
            console.log('[Quill] programDescEditor создан');

            // Инициализация редактора для условий (quote-description)
            console.log('[Quill] Создаём quoteDescEditor...');
            const quoteDescEditor = new Quill('#quote-description-editor', {
                ...editorConfig,
                placeholder: _t('app.quill.placeholder_quote', 'Internal notes, client preferences, important info')
            });
            console.log('[Quill] quoteDescEditor создан');

            // Инициализация редактора для условий бронирования
            console.log('[Quill] Создаём bookingTermsEditor...');
            const bookingTermsEditor = new Quill('#booking-terms-editor', {
                ...editorConfig,
                placeholder: _t('app.quill.placeholder_terms', 'Booking terms, payment deadlines, cancellation rules...')
            });
            console.log('[Quill] bookingTermsEditor создан');

            // Сохраняем ссылки на редакторы
            this.quillEditors = {
                programDescription: programDescEditor,
                quoteDescription: quoteDescEditor,
                bookingTerms: bookingTermsEditor
            };

            // Синхронизация с hidden textarea и автосохранение
            programDescEditor.on('text-change', () => {
                const html = programDescEditor.root.innerHTML;
                document.getElementById('program-description').value = html;
                this.state.programDescription = html;
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();
            });

            quoteDescEditor.on('text-change', () => {
                const html = quoteDescEditor.root.innerHTML;
                document.getElementById('quote-description').value = html;
                this.state.quoteComments = html;
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();
            });

            bookingTermsEditor.on('text-change', () => {
                const html = bookingTermsEditor.root.innerHTML;
                document.getElementById('booking-terms').value = html;
                this.state.bookingTerms = html;
                this.markQuoteAsUnsaved();
                // Автосохранение глобальных настроек
                this.scheduleGlobalSettingsSave();
            });

            // Загружаем сохранённые данные в редакторы
            if (this.state.programDescription) {
                programDescEditor.root.innerHTML = this.state.programDescription;
            }
            if (this.state.quoteComments) {
                quoteDescEditor.root.innerHTML = this.state.quoteComments;
            }
            if (this.state.bookingTerms) {
                bookingTermsEditor.root.innerHTML = this.state.bookingTerms;
            }

            // Добавляем tooltips сразу к DOM элементам редакторов
            // Используем querySelector напрямую на контейнерах редакторов
            this.addQuillTooltips();
        },

        addQuillTooltips() {
            console.log('[Tooltips] Начинаем добавление tooltips...');

            // Tooltips для всех кнопок редактора
            const tooltips = {
                'bold': _t('app.quill.tip_bold', 'Bold (Ctrl+B)'),
                'italic': _t('app.quill.tip_italic', 'Italic (Ctrl+I)'),
                'underline': _t('app.quill.tip_underline', 'Underline (Ctrl+U)'),
                'strike': _t('app.quill.tip_strike', 'Strikethrough'),
                'header': _t('app.quill.tip_header', 'Heading'),
                'list': _t('app.quill.tip_list', 'List'),
                'indent': _t('app.quill.tip_indent', 'Indent'),
                'align': _t('app.quill.tip_align', 'Alignment'),
                'link': _t('app.quill.tip_link', 'Insert link'),
                'clean': _t('app.quill.tip_clean', 'Clear formatting')
            };

            // Находим все контейнеры редакторов
            const editorContainers = ['#program-description-editor', '#quote-description-editor', '#booking-terms-editor'];

            let totalButtons = 0;
            editorContainers.forEach((containerId, index) => {
                const container = document.querySelector(containerId);
                if (!container) {
                    console.warn(`[Tooltips] Контейнер ${containerId} не найден`);
                    return;
                }

                const toolbar = container.previousElementSibling; // Тулбар обычно перед редактором
                if (!toolbar || !toolbar.classList.contains('ql-toolbar')) {
                    // Пробуем найти внутри родителя
                    const parent = container.parentElement;
                    const toolbarInside = parent ? parent.querySelector('.ql-toolbar') : null;

                    if (toolbarInside) {
                        console.log(`[Tooltips] Найден тулбар внутри родителя для ${containerId}`);
                        this.applyTooltipsToToolbar(toolbarInside, tooltips);
                        totalButtons += Object.keys(tooltips).length;
                    } else {
                        console.warn(`[Tooltips] Тулбар не найден для ${containerId}`);
                    }
                } else {
                    console.log(`[Tooltips] Найден тулбар рядом с ${containerId}`);
                    this.applyTooltipsToToolbar(toolbar, tooltips);
                    totalButtons += Object.keys(tooltips).length;
                }
            });

            console.log('[Tooltips] Всего tooltips добавлено:', totalButtons);
        },

        applyTooltipsToToolbar(toolbar, tooltips) {
            // Применяем tooltips к кнопкам в тулбаре
            Object.keys(tooltips).forEach(format => {
                const buttons = toolbar.querySelectorAll(`.ql-${format}`);
                buttons.forEach(button => {
                    // Используем стандартный title для надежности
                    button.setAttribute('title', tooltips[format]);
                });
            });
        }
    });
}
